import api from './api';

function normalizeListResponse(data) {
  return {
    count: Number(data?.count || 0),
    limit: Number(data?.limit || 20),
    offset: Number(data?.offset || 0),
    rows: Array.isArray(data?.rows) ? data.rows : [],
  };
}

function normalizeOrderListResponse(data) {
  const pagination = data?.pagination || {};
  return {
    count: Number(pagination?.count || 0),
    limit: Number(pagination?.limit || 20),
    offset: Number(pagination?.offset || 0),
    rows: Array.isArray(data?.orders) ? data.orders : [],
  };
}

function pruneEmptyParams(params = {}) {
  return Object.entries(params).reduce((acc, [key, value]) => {
    if (value === undefined || value === null || value === '') return acc;
    if (typeof value === 'boolean') {
      if (value) acc[key] = value;
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
}

export async function getSupplyHubProducts(params = {}) {
  const { data } = await api.get('/supply-hub/products', {
    params: pruneEmptyParams(params),
  });
  return normalizeListResponse(data);
}

export async function getSupplyHubProductById(productId) {
  const { data } = await api.get(`/supply-hub/products/${productId}`);
  return data?.product || null;
}

export async function getSupplyHubVendors(params = {}) {
  const { data } = await api.get('/supply-hub/vendors', {
    params: pruneEmptyParams(params),
  });
  return normalizeListResponse(data);
}

export async function getSupplyHubVendorById(vendorId) {
  const { data } = await api.get(`/supply-hub/vendors/${vendorId}`);
  return data?.vendor || null;
}

export async function getSupplyHubRecommendations(params = {}) {
  const { data } = await api.get('/supply-hub/recommendations', {
    params: pruneEmptyParams(params),
  });
  return normalizeListResponse(data);
}

export async function generateCurrentSupplyHubRecommendations() {
  const { data } = await api.post('/supply-hub/recommendations/generate-current');
  return {
    generatedCount: Number(data?.generatedCount || 0),
    skipped: Array.isArray(data?.skipped) ? data.skipped : [],
    rows: Array.isArray(data?.rows) ? data.rows : [],
  };
}

export async function dismissSupplyHubRecommendation(recommendationId, payload = {}) {
  const { data } = await api.post(
    `/supply-hub/recommendations/${recommendationId}/dismiss`,
    payload
  );
  return data?.recommendation || null;
}

export async function getSupplyHubProductMappings(params = {}) {
  const { data } = await api.get('/supply-hub/mappings', {
    params: pruneEmptyParams(params),
  });
  return normalizeListResponse(data);
}

export async function createSupplyHubProductMapping(payload = {}) {
  const { data } = await api.post('/supply-hub/mappings', payload);
  return data?.mapping || null;
}

export async function updateSupplyHubProductMapping(mappingId, payload = {}) {
  const { data } = await api.put(`/supply-hub/mappings/${mappingId}`, payload);
  return data?.mapping || null;
}

export async function archiveSupplyHubProductMapping(mappingId, payload = {}) {
  const { data } = await api.post(`/supply-hub/mappings/${mappingId}/archive`, payload);
  return data?.mapping || null;
}

export async function matchSupplyHubProducts(params = {}) {
  const { data } = await api.get('/supply-hub/match-products', {
    params: pruneEmptyParams(params),
  });
  return {
    currentStock: data?.currentStock || null,
    rows: Array.isArray(data?.rows) ? data.rows : [],
  };
}

export async function createSupplyHubOrder(payload = {}) {
  const { data } = await api.post('/supply-hub/orders', payload);
  return data?.order || null;
}

export async function getSupplyHubOrders(params = {}) {
  const { data } = await api.get('/supply-hub/orders', {
    params: pruneEmptyParams(params),
  });
  return normalizeOrderListResponse(data);
}

export async function getSupplyHubOrderById(orderId) {
  const { data } = await api.get(`/supply-hub/orders/${orderId}`);
  return data?.order || null;
}

export async function cancelSupplyHubOrder(orderId, payload = {}) {
  const { data } = await api.post(`/supply-hub/orders/${orderId}/cancel`, payload);
  return data?.order || null;
}

export async function updateSupplyHubOrderStatus(orderId, payload = {}) {
  const { data } = await api.post(`/supply-hub/orders/${orderId}/status`, payload);
  return data?.order || null;
}

export async function receiveSupplyHubOrder(orderId, payload = {}) {
  const { data } = await api.post(`/supply-hub/orders/${orderId}/receive`, payload);
  return {
    order: data?.order || null,
    receivedItems: Array.isArray(data?.receivedItems) ? data.receivedItems : [],
    stockMovements: Array.isArray(data?.stockMovements) ? data.stockMovements : [],
    message: data?.message || '',
  };
}

export async function receiveSupplyHubOrderItem(orderId, itemId, payload = {}) {
  const { data } = await api.post(
    `/supply-hub/orders/${orderId}/items/${itemId}/receive`,
    payload
  );
  return {
    order: data?.order || null,
    receivedItems: Array.isArray(data?.receivedItems) ? data.receivedItems : [],
    stockMovements: Array.isArray(data?.stockMovements) ? data.stockMovements : [],
    message: data?.message || '',
  };
}

export async function getAdminSupplyHubVendors(params = {}) {
  const { data } = await api.get('/supply-hub/admin/vendors', {
    params: pruneEmptyParams(params),
  });
  return normalizeListResponse(data);
}

export async function getAdminSupplyHubVendorById(vendorId) {
  const { data } = await api.get(`/supply-hub/admin/vendors/${vendorId}`);
  return data?.vendor || null;
}

export async function createAdminSupplyHubVendor(payload = {}) {
  const { data } = await api.post('/supply-hub/admin/vendors', payload);
  return data?.vendor || null;
}

export async function updateAdminSupplyHubVendor(vendorId, payload = {}) {
  const { data } = await api.put(`/supply-hub/admin/vendors/${vendorId}`, payload);
  return data?.vendor || null;
}

export async function approveAdminSupplyHubVendor(vendorId) {
  const { data } = await api.post(`/supply-hub/admin/vendors/${vendorId}/approve`);
  return data?.vendor || null;
}

export async function rejectAdminSupplyHubVendor(vendorId, payload = {}) {
  const { data } = await api.post(`/supply-hub/admin/vendors/${vendorId}/reject`, payload);
  return data?.vendor || null;
}

export async function suspendAdminSupplyHubVendor(vendorId, payload = {}) {
  const { data } = await api.post(`/supply-hub/admin/vendors/${vendorId}/suspend`, payload);
  return data?.vendor || null;
}

export async function getAdminSupplyHubProducts(params = {}) {
  const { data } = await api.get('/supply-hub/admin/products', {
    params: pruneEmptyParams(params),
  });
  return normalizeListResponse(data);
}

export async function getAdminSupplyHubProductById(productId) {
  const { data } = await api.get(`/supply-hub/admin/products/${productId}`);
  return data?.product || null;
}

export async function createAdminSupplyHubProduct(payload = {}) {
  const { data } = await api.post('/supply-hub/admin/products', payload);
  return data?.product || null;
}

export async function updateAdminSupplyHubProduct(productId, payload = {}) {
  const { data } = await api.put(`/supply-hub/admin/products/${productId}`, payload);
  return data?.product || null;
}

export async function approveAdminSupplyHubProduct(productId) {
  const { data } = await api.post(`/supply-hub/admin/products/${productId}/approve`);
  return data?.product || null;
}

export async function rejectAdminSupplyHubProduct(productId, payload = {}) {
  const { data } = await api.post(`/supply-hub/admin/products/${productId}/reject`, payload);
  return data?.product || null;
}

export async function sponsorAdminSupplyHubProduct(productId, payload = {}) {
  const { data } = await api.post(`/supply-hub/admin/products/${productId}/sponsor`, payload);
  return data?.product || null;
}

export async function unsponsorAdminSupplyHubProduct(productId) {
  const { data } = await api.post(`/supply-hub/admin/products/${productId}/unsponsor`);
  return data?.product || null;
}
